import { DataGridFeatureConfig } from '@waseel-asp/tpa-waseel-lib-staging';
import { defaultGridFeatures } from '../../../../../../shared/constants/grid-feature-config';
import { IBenefit } from '../../benefit/models/benefit.model';
import cloneDeep from 'lodash/cloneDeep';
import { PatientShareType } from '../../../../../../shared/constants/patient-share-enum';

const gridFeatures: DataGridFeatureConfig<IBenefit | ISubCoverage> = cloneDeep(defaultGridFeatures);
export const addEditgridFeatures: DataGridFeatureConfig<IBenefit> = {
  ...gridFeatures,
  showPagination: false,
  windowResizeListner: false,
  isDefaultEditEnabled: true,
  allowSorting: false,
  allowExpandRow: true,
};

export const addEditSubgridFeatures: DataGridFeatureConfig<ISubCoverage> = {
  ...gridFeatures,
  showPagination: false,
  enableApiSorting: false,
  enableApiSearching: false,
  allowSorting: false,
  isDummyColumn: true,
  allowHideShowColumns: false,
  hideTableHeader: false,
  isDefaultEditEnabled: true,
  paginatorFeatures: {
    showRefreshButton: false,
    enableApiPagination: false
  }
};

export interface RoomTypeModel {
  id: string;
  nameEnglish: string;
  nameArabic: string;
}

export interface IPolicyClass {
  classId: string;
  insuranceCompanyId: string;
  insuranceCompanyNameEnglish: string;
  insuranceCompanyNameArabic: string;
  networkDetails: INetwork[];
  classCodeEnglish: string;
  classCodeArabic: string;
  classNameEnglish: string;
  classNameArabic: string;
  classLimit: number;
  roomTypeId: string;
  roomTypeNameEnglish: string;
  roomTypeNameArabic: string;
  roomLimit: number;
  coverage: string;
  exclusions: string;
  comments: string;
  deleted: boolean;
  createdBy: string;
  createdDate: string;
  lastUpdatedBy: string;
  lastUpdatedDate: string;
  deletedBy: string | null;
  deletedDate: string | null;
  classCaseTypes: IClassCaseType[];
  subrogation: boolean;
  patientShareType: PatientShareType;
  patientShareTypeDesc: string;
  patientShare: number;
  maxPatientShare: number;
  isPolicyClassAssigned?: boolean;
  caseTypes?: IClassCaseType[];
  erpClassId?: string;
}

interface INetwork {
  networkId: string;
  codeEnglish: string;
  codeArabic: string;
  nameEnglish: string;
  nameArabic: string;
  descriptionEnglish: string;
  descriptionArabic: string;
  deleted: boolean;
  createdBy: string;
  createdDate: string;
  lastUpdatedBy: string;
  lastUpdatedDate: string;
  deletedBy: string | null;
  deletedDate: string | null;
}

export interface IClassCaseType {
  caseTypeId: string;
  caseTypeNameEnglish: string;
  caseTypeNameArabic: string;
  coverageCondition: ICoverageCondition | null;
  classBenefits?: IClassBenefit[];
  benefits?: IClassBenefit[];
}

export interface IClassBenefit {
  coverageCondition: ICoverageCondition | null;
  benefitSubCoverages: IBenefitSubCoverage[];
  benefitId: string | null;
  code: string;
  nameEnglish: string;
  nameArabic: string;
  deductible: number;
  roomTypeNameEnglish: string,
  roomTypeNameArabic: string,
  visit: number;
  roomTypeId: string;
  visionExam: string;
  visionGlasses: string;
  visionContacts: string;
  medicalPrimaryCare: string;
  pharmacyDispense: string;
  descriptionEnglish: string;
  descriptionArabic: string;
  benefitLimit: number;
  patientShareType: string | null;
  patientShareTypeDesc: string | null;
  patientShare: number | null;
  maxPatientShare: number | null;
  maxConsultationFee: number | null;
  approvalThreshold: number | null;
  coverage: string;
  exclusions: string;
  comments: string | null;
  deleted: boolean;
  createdBy: string | null;
  createdDate: string;
  lastUpdatedBy: string | null;
  lastUpdatedDate: string;
  deletedBy: string | null;
  deletedDate: string | null;
  subCoverages: ISubCoverage[] | null;
  networkTypeId: string;
  networkTypeNameEnglish: string;
  networkTypeNameArabic: string;
  termId: string;
  termNameEnglish: string;
  termNameArabic: string;
}

export interface IBenefitSubCoverage {
  coverageCondition: ICoverageCondition | null;
  subCoveragesId: string;
  benefitId: string | null;
  codeEnglish: string;
  codeArabic: string;
  descriptionEnglish: string;
  descriptionArabic: string;
  subCoverageLimit: number;
  approvalThreshold: number;
  deleted: boolean;
  createdBy: string | null;
  createdDate: string;
  lastUpdatedBy: string | null;
  lastUpdatedDate: string;
  deletedBy: string | null;
  deletedDate: string | null;
}

export interface ICoverageCondition {
  policyBenefitCoverageConId: string;
  policyClassBenefitId: string;
  coverageConditionFor: number;
  benefitLimit: number | null;
  deductible: number | null;
  visit: string | null;
  roomTypeId: string | null;
  roomTypeNameEnglish: string | null;
  roomTypeNameArabic: string | null;
  patientShareType: string | null;
  patientShareTypeDesc: string | null;
  patientShare: number | null;
  maxPatientShare: number | null;
  visionExam: string | null;
  visionGlasses: string | null;
  visionContacts: string | null;
  medicalPrimaryCare: string | null;
  pharmacyDispense: string | null;
  approvalThreshold: number | null;
  remark: string | null;
  createdBy: string | null;
  createdDate: string;
}

export interface ISubCoverage {
  coverageCondition: ICoverageCondition | null;
  subCoveragesId: string;
  benefitId: string | null;
  codeEnglish: string;
  codeArabic: string;
  descriptionEnglish: string;
  descriptionArabic: string;
  subCoverageLimit: number;
  approvalThreshold: number;
  isCovered: boolean;
  deleted: boolean;
  createdBy: string | null;
  createdDate: string;
  lastUpdatedBy: string | null;
  lastUpdatedDate: string;
  deletedBy: string | null;
  deletedDate: string | null;
}

export const policyClassConfig = {
  entityName: 'CONFIGURATION.POLICY_CLASS.TITLE',
  classCodeEnglish: 'FORM_FIELD.ENGLISH_CODE',
  classNameEnglish: 'FORM_FIELD.ENGLISH_NAME',
  classCodeArabic: 'FORM_FIELD.ARABIC_CODE',
  classNameArabic: 'FORM_FIELD.ARABIC_NAME',
  classLimit: 'FORM_FIELD.LIMIT',
  roomLimit: 'FORM_FIELD.ROOM_LIMIT',
  roomTypeId: 'CONFIGURATION.MASTER_DATA.ROOM_TYPE.TITLE',
  benefit: 'benefit',
  subcoverage: 'subCoverage',
  caseType: 'caseType'
};